import { Image } from 'semantic-ui-react'
import { getCurrentUser } from '../../../selectors/user.selectors'
import { useReselector } from '../../../utils/sharedHooks'
import styled from 'styled-components'
import { Text, Icon } from '../../../components/BaseComponents'
import { DeviceWidth, useIsDeviceWidth } from '@/src/utils/deviceWidthHelpers'
import { Colors } from '../../../styles/theme'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'

const StyledWelcomeSection = styled.div`
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  &.mobile {
    flex-direction: column;
    gap: 24px;
  }
`

const WelcomeContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 600px;
`

const WelcomeImage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e4f2e6;
  border-radius: 16px;
  padding: 24px;
`

const StyledInsightsSection = styled.div`
  display: flex;
  gap: 40px;

  &.mobile {
    flex-direction: column;
  }
`

const SubSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 600px;
`

const SetupSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const StepContainer = styled.div`
  display: flex;
  gap: 24px;
  padding: 24px;
  align-items: center;
  background-color: ${Colors.stone40};
`

const StepCard = styled.div`
  display: flex;
  gap: 24px;
  border-radius: 8px;
  align-items: flex-start;
`

const StepNumberCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background-color: ${Colors.moss};
  color: ${Colors.black};
  border-radius: 50%;
`

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`

const StepHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`

const StepDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const TimeContainer = styled.div`
  display: flex;
  align-items: center;
`

const steps = [
  {
    title: 'Connect your business accounts',
    description:
      'Link your business bank accounts or credit cards so we can automatically organize your transactions.',
    duration: '5 minutes',
    image: 'https://heard-images.s3.amazonaws.com/assets/bank-yellow.svg',
  },
  {
    title: 'Tell us about your accounts',
    description:
      'Let us know if there are any special considerations around your accounts. This helps us optimize your bookkeeping.',
    duration: '5 minutes',
    image: 'https://heard-images.s3.amazonaws.com/assets/laptop-only.png',
  },
  {
    title: 'Upload past month bank statements',
    description:
      "Provide statements so our team can verify your books' accuracy, spot tax deductions, and ensure you're always audit-ready.",
    duration: '10 minutes',
    image: 'https://heard-images.s3.amazonaws.com/assets/upload-statement.png',
  },
]

export const NewObSplashScreen = () => {
  const currentUser = useReselector(getCurrentUser)
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)

  return (
    <>
      <WelcomeImage>
        <Image
          src="https://heard-images.s3.amazonaws.com/assets/book-celebration.svg"
          alt=""
          style={{ width: '180px', height: 'auto' }}
        />
      </WelcomeImage>

      <StyledWelcomeSection className={`${isMobile && 'mobile'}`}>
        <WelcomeContent>
          <Text as="display2">
            Welcome to Heard
            {currentUser?.firstName ? `, ${currentUser?.firstName}` : ''}!
          </Text>
          <Text as="bodyMd">
            We&apos;re so excited you&apos;re here. At Heard, we&apos;re on a
            mission to lighten the mental load of running your practice, so you
            can focus on what you do best.
          </Text>
        </WelcomeContent>
      </StyledWelcomeSection>

      <StyledInsightsSection className={`${isMobile && 'mobile'}`}>
        <SubSection>
          <Text as="display3">
            You&apos;re close to unlocking your practice insights
          </Text>
          <Text as="bodyMd">
            Your dashboard is your go-to hub for financial clarity and
            confidence. We analyze your transactions to provide instant insights
            on your business.
          </Text>
        </SubSection>
        <Image
          src="https://heard-images.s3.amazonaws.com/assets/chartting.png"
          alt=""
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '16px',
            boxShadow: '0px 4px 12px 0px #00000026',
            borderRadius: '8px',
            width: isMobile ? '100%' : '306px',
            objectFit: 'contain',
          }}
        />
      </StyledInsightsSection>

      <SubSection>
        <Text as="display3">Finish account setup</Text>
        <Text as="bodyMd">
          Your insights will become available once we finish setting up your
          account. These steps ensure your books are accurate, complete, and
          ready for analysis.
        </Text>
      </SubSection>

      <SetupSection>
        {steps.map((step, index) => (
          <StepContainer key={step.title}>
            <StepCard>
              <StepNumberCircle>{index + 1}</StepNumberCircle>
              <StepContent>
                <StepHeader>
                  <StepDetails>
                    <Text as="h3">{step.title}</Text>
                    <Text as="bodySm" style={{ color: Colors.charcoal }}>
                      {step.description}
                    </Text>
                    <TimeContainer>
                      <Icon
                        icon={regular('timer')}
                        style={{ marginRight: 4, color: Colors.oak }}
                        size="sm"
                      />
                      <Text as="bodyXs" style={{ color: Colors.oak }}>
                        {step.duration}
                      </Text>
                    </TimeContainer>
                  </StepDetails>
                </StepHeader>
              </StepContent>
            </StepCard>

            <Image
              src={step.image}
              alt=""
              style={{ width: '116px', objectFit: 'contain' }}
            />
          </StepContainer>
        ))}
      </SetupSection>
    </>
  )
}
